<template>
    <div class="row">
	    <VeeForm :validation-schema="checkoutValidationSchema" :initial-values="initialValues" v-slot="{ handleSubmit, errors }" @invalid-submit="onInvalidSubmit" as="div">
            <form ref="form" @submit="handleSubmit($event, onCheckoutSubmit)" method="post" :action="this.baseUrl + '/process-order'" class="row">
                <div class="col-12 col-lg-8">
                    <div class="row">
                        <div class="col-12">
                            <billing-details
                                :basket="basket"
                                :registration="sessionReg"
                                :postcode="sessionPostcode"
                                :addresses="billingAddresses"
                                :errors="errors"
                                @inputAddressManually="inputAddressManually = !inputAddressManually"
                            ></billing-details>
                        </div>
                        <div class="col-12">
                            <div :class="fitter.mobile == 0 ? 'd-none' : 'border-thin rounded-lg p-4 px-3 mt-4'">
                                <address-details
                                    :fitter="fitter"
                                    :postcode="sessionPostcode"
                                    :addresses="shippingAddresses"
                                    :same-as-billing="this.same_as_billing"
                                    :errors="errors"
                                    @inputShippingAddressManually="inputShippingAddressManually = !inputShippingAddressManually"
                                    @toggleShippingFields="this.same_as_billing = !this.same_as_billing;"
                                ></address-details>
                            </div>
                        </div>
                        <div class="col-12 mb-0 mb-2 mb-lg-0" v-if="fitter">
                            <fitting-details
                                :basket-app-date="basket.app_date"
                                :basket-app-time="basket.app_time"
                                :basket-app-end-time="basket.app_end_time"
                                :fitter="fitter"
                                :postcode-details="postcodeDetails"
                                :google-maps-api-key="googleMapsApiKey"
                                :errors="errors"
                                :custom-slot-count="customSlotCount"
                                :mobile-fitting-slots="mobileFittingSlots"
                                :totd-fitting-slots="totdFittingSlots"
                            ></fitting-details>
                            <Field v-if="validateAppDate" as="hidden" name="validateAppDate" :value="validateAppDate"></Field>
                            <Field v-if="validateAppTime" as="hidden" name="validateAppTime" :value="validateAppTime"></Field>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-4 mt-3 mt-lg-0">
                    <div class="border-thin rounded-lg p-4 px-3">
                        <div class="row position-relative">
                            <h2 class="raised-label">Your order</h2>
                        </div>
                        <order-details :basket="updatedBasket" :services="fitter.services"></order-details>
                        <ErrorMessage name="agreed_conditions">
                            <span class="error"></span>
                        </ErrorMessage>
                        <Popper :content="errors.agreed_conditions" arrow placement="top" class="m-0 border-0" :show="errors.agreed_conditions ? true : false">
                            <Field id="agreed_conditions" name="agreed_conditions" type="checkbox" :value="true" :unchecked-value="false" />
                            <label class="my-2 cursor-pointer">
                                I have read and accept the <a :href="this.baseUrl + '/terms'" title="Terms & Conditions" class="text-secondary" target="_blank">terms and conditions</a>.
                            </label>
                        </Popper>
                        <Field id="payment_method" as="hidden" name="payment_method" v-model="this.paymentMethod" />
                        <div class="payment-options d-block text-center mt-3">
                            <i class="fa-brands fa-cc-visa fa-2x me-2 opacity-50" alt="Visa accepted" title="Visa accepted"></i>
                            <i class="fa-brands fa-cc-mastercard fa-2x me-2 opacity-50" alt="Mastercard and Maestro accepted" title="Mastercard and Maestro accepted"></i>
                            <i class="fa-brands fa-google-pay fa-2x me-2 opacity-50" alt="Google Pay accepted" title="Google Pay accepted"></i>
                            <template v-if="payWithPaypal">
                                <i class="fa-brands fa-paypal fa-2x opacity-50" alt="Paypal Accepted" title="Paypal Accepted"></i>
                            </template>
                        </div>
                        <button data-payment="card" type="submit" class="btn btn-primary form-control mt-3 py-2 fs-6">Pay by Card</button>
                        <template v-if="payWithPaypal">
                            <span class="text-center fw-bold d-block mx-auto my-2 text-navy">OR</span>
                            <button data-payment="paypal" data-button-text="" type="submit" name="paypal" class="btn btn-warning form-control fs-6 paypal-button" @click="this.paymentMethod = 'paypal' ">
                                <img :src="this.baseUrl + '/assets/img/generic_images/paypal.png'" alt="Paypal" />
                            </button>
                            <template v-if="showPaypalBanners">
                                <div class="pp-banner mt-2" data-pp-message :data-pp-amount="updatedBasket.total" data-pp-placement="payment" data-pp-style-layout="text" data-pp-style-logo-type="inline"></div>
                            </template>
                        </template>
                    </div>
                </div>
            </form>
        </VeeForm>
        <span v-if="payment == 'online'" id="payment-submit" ref="paymentSubmit"></span>
        <div v-if="payment == 'online'" id='payment-fancybox'></div>
        <teleport to="body">
			<service-modal :service="serviceData" :basket="updatedBasket" :service-modal-open="serviceModalOpen" @closeModal="serviceModalOpen = false"></service-modal>
            <info-modal></info-modal>
		</teleport>
    </div>
</template>
<script>
    import { Form as VeeForm, Field, ErrorMessage } from 'vee-validate';
    import * as yup from 'yup';
    import checkout from '@shared/mixins/checkout';

    export default {
        props: [
            'basket',
            'fitter',
            'sessionReg',
            'sessionPostcode',
            'addresses',
            'websiteUrl',
            'payment',
            'postcodeDetails',
            'googleMapsApiKey',
            'customSlotCount',
            'mobileFittingSlots',
            'totdFittingSlots',
            'payWithPaypal',
            'showPaypalBanners'
        ],
        mixins: [checkout],
        components: {
            VeeForm, Field, ErrorMessage
        },
        data() {
            let checkoutValidationSchema = yup.object({
                // Billing/Shipping fields
                forename: yup.string().required(),
                surname: yup.string().required(),
                email: yup.string().email().required(),
                telephone: yup.string().required(),
                registration: yup.string().required('registration is a required field').nullable(),
                postcode: yup.string().required().nullable(),
                address: yup.string().when(['input_address_manually'], {
                is: (input_address_manually) => input_address_manually != true,
                then: yup.string().required('Address is required'),
                }),
                address1: yup.string()
                .when('input_address_manually', {
                    is: true,
                    then: yup.string().required()
                }),
                // address2: yup.string()
                // .when('input_address_manually', {
                //     is: true,
                //     then: yup.string().required()
                // }),
                // address3: yup.string()
                // .when('input_address_manually', {
                //     is: true,
                //     then: yup.string().required()
                // }),
                town: yup.string()
                .when('input_address_manually', {
                    is: true,
                    then: yup.string().required()
                }),
                // county: yup.string()
                // .when('input_address_manually', {
                //     is: true,
                //     then: yup.string().required()
                // }),
                no_mail: yup.number().integer(),
                // Fitting appointment fields
                app_date: yup.string()
                .when('validateAppDate', {
                    is: true,
                    then: yup.string().required('App Date is a required field').nullable()
                }),
                app_time: yup.string()
                .when('validateAppTime', {
                    is: true,
                    then: yup.string().required()
                }),
                // Shipping fields
                // same_as_billing: yup.boolean(),
                shipping_forename: yup.string()
                .when('same_as_billing', {
                    is: 'on',
                    then: yup.string().required('Shipping Forename is a required field')
                }),
                shipping_surname: yup.string()
                .when('same_as_billing', {
                    is: 'on',
                    then: yup.string().required('Shipping Surname is a required field')
                }),
                shipping_postcode: yup.string()
                .when('same_as_billing', {
                    is: 'on',
                    then: yup.string().required('Shipping Postcode is a required field')
                }),
                shipping_address: yup.string().when(['same_as_billing', 'input_shipping_address_manually'], {
                is: (same_as_billing, input_shipping_address_manually) => same_as_billing == 'on' && input_shipping_address_manually != true,
                then: yup.string().required('Shipping Address is required'),
                }),
                shipping_address1: yup.string()
                .when('input_shipping_address_manually', {
                    is: true,
                    then: yup.string().required('Address1 is a required field')
                }),
                // shipping_address2: yup.string()
                // .when('input_shipping_address_manually', {
                //     is: true,
                //     then: yup.string().required('Address2 is a required field')
                // }),
                // shipping_address3: yup.string()
                // .when('input_shipping_address_manually', {
                //     is: true,
                //     then: yup.string().required('Address3 is a required field')
                // }),
                shipping_town: yup.string()
                .when('input_shipping_address_manually', {
                    is: true,
                    then: yup.string().required('Town is a required field')
                }),
                // shipping_county: yup.string()
                // .when('input_shipping_address_manually', {
                //     is: true,
                //     then: yup.string().required('County is a required field')
                // }),
                //Order fields
                agreed_conditions: yup.string().required('You must accept the terms and conditions to continue.')
            });

            return {
                registration: this.sessionReg != null ? this.sessionReg : '',
                postcode: this.sessionPostcode != null ? this.sessionPostcode : '',
                serviceModalOpen: false,
                billingAddresses: this.addresses,
                shippingAddresses: this.addresses,
                inputAddressManually: false,
                inputShippingAddressManually: false,
                //Shipping fields,
                same_as_billing: (this.basket?.home_delivery == 1 || this.fitter?.mobile == 1) ? true : false,
                //Order fields
                agreed_conditions: false,
                paymentMethod: 'card',
                checkoutValidationSchema,
                validateAppDate: this.basket?.home_delivery == 0,
                validateAppTime: (this.basket?.home_delivery == 0 && (this.fitter?.mobile == 0 || this.fitter?.cal_account_number == 'CALTOTD')),
                initialValues: {
                    forename: this.basket?.billing_forename || '',
                    surname: this.basket?.billing_surname || '',
                    email: this.basket?.email || '',
                    postcode: this.basket?.billing_postcode || (this.sessionPostcode || ''),
                    address1: this.basket?.billing_address1 || '',
                    address2: this.basket?.billing_address2 || '',
                    address3: this.basket?.billing_address3 || '',
                    town: this.basket?.billing_town || '',
                    county: this.basket?.billing_county || '',
                    telephone: this.basket?.billing_telephone || '',
                    registration: this.sessionReg || '',
                    shipping_title: this.basket?.shipping_title || '',
                    shipping_forename: this.basket?.shipping_forename || '',
                    shipping_surname: this.basket?.shipping_surname || '',
                    shipping_postcode: this.basket?.shipping_postcode || '',
                    shipping_address1: this.basket?.shipping_address1 || '',
                    shipping_address2: this.basket?.shipping_address2 || '',
                    shipping_address3: this.basket?.shipping_address3 || '',
                    shipping_town: this.basket?.shipping_town || '',
                    shipping_county: this.basket?.shipping_county || '',
                    shipping_telephone: this.basket?.shipping_telephone || '',
                    payment_method: this.paymentMethod
                }
            }
        },
        methods: {
            onInvalidSubmit({ values, errors, results }) {
                //Focus first invalid field
                var firstInvalidField = document.getElementsByName(Object.keys(errors)[0]);
                firstInvalidField[0].focus();
            }
        }
    }
</script>
<style lang="scss" scoped>
    .terms-link {
        color: #39AAE1;
    }
    .paypal-button {
        padding: 5px;
    }
</style>